/**
 * LocalStorage abstraction layer with error handling, versioning, and quota management
 * Centralizes all localStorage operations to eliminate duplication and improve reliability
 */

import { STORAGE_KEYS } from '@/config/constants';

// Storage version for migration support
const STORAGE_VERSION = '1.0';
const VERSION_KEY = 'storage_version';

/**
 * Safe localStorage getter with error handling
 * @param {string} key - The storage key
 * @param {*} defaultValue - Default value if key doesn't exist or error occurs
 * @returns {*} The parsed value or default
 */
export const getItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;

    try {
      return JSON.parse(item);
    } catch {
      // If JSON parse fails, return the raw string
      return item;
    }
  } catch (error) {
    console.error(`Error reading from localStorage (key: ${key}):`, error);
    return defaultValue;
  }
};

/**
 * Safe localStorage setter with error handling and quota management
 * @param {string} key - The storage key
 * @param {*} value - The value to store (will be JSON stringified)
 * @returns {boolean} Success status
 */
export const setItem = (key, value) => {
  try {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.error('LocalStorage quota exceeded. Attempting cleanup...');
      cleanupOldData();

      // Retry after cleanup
      try {
        const serialized = typeof value === 'string' ? value : JSON.stringify(value);
        localStorage.setItem(key, serialized);
        return true;
      } catch (retryError) {
        console.error('Failed to store data even after cleanup:', retryError);
        return false;
      }
    } else {
      console.error(`Error writing to localStorage (key: ${key}):`, error);
      return false;
    }
  }
};

/**
 * Remove an item from localStorage
 * @param {string} key - The storage key
 * @returns {boolean} Success status
 */
export const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage (key: ${key}):`, error);
    return false;
  }
};

/**
 * Clear all localStorage data
 * @returns {boolean} Success status
 */
export const clear = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Check if a key exists in localStorage
 * @param {string} key - The storage key
 * @returns {boolean}
 */
export const hasItem = (key) => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error(`Error checking localStorage (key: ${key}):`, error);
    return false;
  }
};

/**
 * Get the current storage usage
 * @returns {Object} Storage usage info { used, total, percentage }
 */
export const getStorageUsage = () => {
  try {
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }

    // Most browsers limit localStorage to ~5-10MB
    const estimatedLimit = 5 * 1024 * 1024; // 5MB
    const percentage = (totalSize / estimatedLimit) * 100;

    return {
      used: totalSize,
      total: estimatedLimit,
      percentage: percentage.toFixed(2),
    };
  } catch (error) {
    console.error('Error calculating storage usage:', error);
    return { used: 0, total: 0, percentage: 0 };
  }
};

/**
 * Cleanup old or unnecessary data to free up space
 * Priority: Remove oldest cache data first
 */
const cleanupOldData = () => {
  try {
    // Remove known cache keys that can be regenerated
    const cacheKeys = ['trackerData', 'chatRooms', 'pendingReviews'];

    for (const key of cacheKeys) {
      if (hasItem(key)) {
        console.log(`Removing cached data: ${key}`);
        removeItem(key);
      }
    }

    console.log('LocalStorage cleanup completed');
  } catch (error) {
    console.error('Error during cleanup:', error);
  }
};

/**
 * Initialize storage version for migration support
 */
export const initializeStorage = () => {
  const currentVersion = getItem(VERSION_KEY);

  if (!currentVersion) {
    setItem(VERSION_KEY, STORAGE_VERSION);
    console.log('Storage initialized with version:', STORAGE_VERSION);
  } else if (currentVersion !== STORAGE_VERSION) {
    console.log(`Storage version mismatch. Current: ${currentVersion}, Expected: ${STORAGE_VERSION}`);
    // Future: Add migration logic here if needed
  }
};

// Domain-specific storage helpers

/**
 * Get user profile from storage
 * @returns {Object|null}
 */
export const getUserProfile = () => {
  return getItem(STORAGE_KEYS.userProfile, null);
};

/**
 * Set user profile in storage
 * @param {Object} profile
 * @returns {boolean}
 */
export const setUserProfile = (profile) => {
  return setItem(STORAGE_KEYS.userProfile, profile);
};

/**
 * Get authentication status
 * @returns {boolean}
 */
export const getAuthStatus = () => {
  const authStatus = getItem(STORAGE_KEYS.isAuthenticated, 'false');
  return authStatus === 'true' || authStatus === true;
};

/**
 * Set authentication status
 * @param {boolean} isAuthenticated
 * @returns {boolean}
 */
export const setAuthStatus = (isAuthenticated) => {
  return setItem(STORAGE_KEYS.isAuthenticated, isAuthenticated ? 'true' : 'false');
};

/**
 * Get resume data
 * @returns {Object|null}
 */
export const getResumeData = () => {
  return getItem(STORAGE_KEYS.resumeData, null);
};

/**
 * Set resume data
 * @param {Object} resumeData
 * @returns {boolean}
 */
export const setResumeData = (resumeData) => {
  return setItem(STORAGE_KEYS.resumeData, resumeData);
};

/**
 * Clear all user data (useful for logout)
 * @returns {boolean}
 */
export const clearUserData = () => {
  try {
    removeItem(STORAGE_KEYS.userProfile);
    removeItem(STORAGE_KEYS.isAuthenticated);
    removeItem(STORAGE_KEYS.resumeData);
    removeItem(STORAGE_KEYS.pendingReviews);
    removeItem(STORAGE_KEYS.trackerData);
    console.log('User data cleared from localStorage');
    return true;
  } catch (error) {
    console.error('Error clearing user data:', error);
    return false;
  }
};

// Initialize storage on module load
initializeStorage();
